import React, { useEffect, useState } from "react";
import API_BASE from "../config/api";
import { useSearchParams } from "react-router-dom";

export default function Wallet({ balance, onBalanceUpdate, personaluid, listinguid, listings = [] }) {

    // CR A1 — one wallet per LOGIN now, not per business listing.
    const userId = personaluid || listinguid; // fallback keeps this working even if a caller hasn't been updated yet

    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddFund, setShowAddFund] = useState(false);
    const [addAmount, setAddAmount] = useState("");
    const [payLoading, setPayLoading] = useState(false);

    const [searchParams] = useSearchParams();
    const paymentStatus = searchParams.get("payment");
    const [note, setNote] = useState("");

    const [toast, setToast] = useState({ show: false, message: "", type: "success", duration: 3000 });

    useEffect(() => {
        if (!userId) { setLoading(false); return; }
        fetch(`${API_BASE}/api/get_wallet.php?user_id=${userId}`)
            .then(res => res.json())
            .then(data => {
                if (data.success) { onBalanceUpdate(data.balance || 0); setHistory(data.history || []); }
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [userId, onBalanceUpdate]);

    const refreshWallet = () => {
        if (!userId) return;
        fetch(`${API_BASE}/api/get_wallet.php?user_id=${userId}`)
            .then(res => res.json())
            .then(data => { if (data.success) { onBalanceUpdate(data.balance || 0); setHistory(data.history || []); } })
            .catch(() => { });
    };

    const handleAddFund = async () => {
    if (!addAmount || addAmount <= 0) return;
    setPayLoading(true);
    try {
        const res = await fetch(`${API_BASE}/api/wallet_generate_fonepay.php`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ user_id: userId, amount: addAmount, note: note || null })
        });
        const data = await res.json();

        if (data.success && data.payment_url) {
            // External gateway flow — leaving the page, no need to reset local state
            window.location.href = data.payment_url;
            return;
        }

        if (data.success) {
            // Instant success (no external redirect needed) — close modal, reset fields, show toast
            setShowAddFund(false);
            setAddAmount("");
            setNote("");
            setToast({ show: true, message: data.message || "Wallet topped up successfully 💰", type: "success", duration: 3500 });
            refreshWallet();
        } else {
            setToast({ show: true, message: data.message || "Unable to initiate payment", type: "danger", duration: 3500 });
        }
    } catch {
        setToast({ show: true, message: "Network error", type: "danger", duration: 3500 });
    }
    setPayLoading(false);
};

    useEffect(() => {
        if (!toast.show) return;
        const timer = setTimeout(() => {
            setToast(prev => ({ ...prev, show: false }));
            const url = new URL(window.location.href);
            url.searchParams.delete("payment"); url.searchParams.delete("page");
            window.history.replaceState({}, "", url);
        }, toast.duration);
        return () => clearTimeout(timer);
    }, [toast.show]);

    useEffect(() => {
        if (!paymentStatus) return;
        if (paymentStatus === "success") setToast({ show: true, message: "Wallet topped up successfully 💰", type: "success", duration: 3500 });
        if (paymentStatus === "failed") setToast({ show: true, message: "Wallet top-up failed ✗", type: "danger", duration: 3500 });
    }, [paymentStatus]);

    const txnIcon = (row) => {
        const t = (row.title || "").toLowerCase();
        if (t.includes("transfer")) return row.type === "credit" ? "bi-box-arrow-in-down" : "bi-box-arrow-up";
        if (t.includes("upgrade")) return "bi-stars";
        if (t.includes("top-up") || t.includes("topup") || t.includes("fonepay")) return "bi-plus-circle";
        return row.type === "credit" ? "bi-arrow-down-circle" : "bi-arrow-up-circle";
    };

    return (
        <div className="wl-wrap">
            {toast.show && (
                <div className={`custom-toast ${toast.type}`}>
                    <div className="toast-icon">{toast.type === "success" && "✓"}{toast.type === "danger" && "✗"}{toast.type === "info" && "ℹ"}</div>
                    <div className="toast-content">
                        <div className="toast-title">{toast.type === "success" ? "Success" : toast.type === "danger" ? "Error" : "Info"}</div>
                        <div className="toast-message">{toast.message}</div>
                    </div>
                    <button className="toast-close" onClick={() => setToast({ ...toast, show: false })}>×</button>
                    <div className="toast-progress" style={{ animationDuration: `${toast.duration}ms` }} />
                </div>
            )}

            {/* BALANCE HERO */}
            <div className="wl-hero">
                <div className="wl-hero-left">
                    <div className="wl-hero-icon"><i className="bi bi-wallet2"></i></div>
                    <div>
                        <div className="wl-hero-label">Available Balance · your account</div>
                        <div className="wl-hero-amount">₹ {Number(balance).toLocaleString()}</div>
                        <div className="wl-hero-sub">Shared across all your businesses — used for promotions & upgrades</div>
                    </div>
                </div>
                <div className="wl-hero-actions">
                    <button className="wl-btn wl-btn-solid" onClick={() => setShowAddFund(true)}><i className="bi bi-plus-circle me-1"></i> Add Funds</button>
                </div>
            </div>

            {/* HISTORY */}
            <div className="wl-card">
                <div className="wl-card-head">
                    <span><i className="bi bi-clock-history me-2 text-primary"></i>Wallet / Reward History</span>
                    <span className="wl-count">{history.length}</span>
                </div>

                {loading ? (
                    <div className="text-center py-5 text-muted">
                        <div className="wl-spinner"></div>
                        <p className="mt-3 mb-0">Loading wallet history…</p>
                    </div>
                ) : history.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                        <i className="bi bi-receipt" style={{ fontSize: "2rem", color: "#c7d0dd" }}></i>
                        <p className="mt-2 mb-0">No wallet transactions found</p>
                    </div>
                ) : (
                    <div className="table-responsive">
                        <table className="wl-table">
                            <thead><tr><th>Transaction</th><th>Date</th><th className="text-end">Amount</th><th>Status</th></tr></thead>
                            <tbody>
                                {history.map((row, i) => (
                                    <tr key={i}>
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                <div className={`wl-txn-icon ${row.type === "credit" ? "credit" : "debit"}`}><i className={`bi ${txnIcon(row)}`}></i></div>
                                                <div style={{ minWidth: 0 }}>
                                                    <div className="fw-semibold text-dark">{row.title}</div>
                                                    {row.reference && <small className="text-muted">Ref: {row.reference}</small>}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="text-muted small">{row.date}</td>
                                        <td className={`text-end fw-bold ${row.type === "credit" ? "text-success" : "text-danger"}`}>
                                            {row.type === "credit" ? "+" : "−"} ₹ {row.amount}
                                        </td>
                                        <td><span className={`wl-pill ${row.status === "SUCCESS" ? "ok" : row.status === "PENDING" ? "pend" : "fail"}`}>{row.status}</span></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* ADD FUND MODAL */}
            {showAddFund && (
                <>
                    <div className="modal show fade" style={{ display: "block" }} tabIndex="-1">
                        <div className="modal-dialog modal-lg modal-dialog-centered">
                            <div className="modal-content border-0 shadow-lg rounded-4">
                                <div className="modal-header bg-success text-white rounded-top-4">
                                    <h5 className="modal-title d-flex align-items-center gap-2"><i className="bi bi-wallet2 fs-4"></i> Add Funds to Wallet</h5>
                                    <button type="button" className="btn-close btn-close-white" onClick={() => setShowAddFund(false)} />
                                </div>
                                <div className="modal-body px-4 py-4">
                                    <div className="row g-3">
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold">Amount (NPR)</label>
                                            <input type="text" className={`form-control form-control-lg ${addAmount !== "" && Number(addAmount) < 10 ? "is-invalid" : ""}`}
                                                placeholder="Enter amount" value={addAmount} onChange={(e) => setAddAmount(e.target.value.replace(/\D/g, ""))} />
                                            {addAmount !== "" && Number(addAmount) < 10
                                                ? <div className="invalid-feedback d-block">Minimum top-up amount is NPR 10</div>
                                                : <small className="text-muted">Minimum top-up amount is NPR 10</small>}
                                        </div>
                                        <div className="col-md-6">
                                            <label className="form-label fw-semibold">Note / Reference <span className="text-muted">(optional)</span></label>
                                            <input type="text" className="form-control form-control-lg" placeholder="Eg: Promotion budget, Ads, etc." value={note} onChange={(e) => setNote(e.target.value)} />
                                            <small className="text-muted">This is only for your reference</small>
                                        </div>
                                    </div>
                                    <div className="alert alert-light border mt-4 mb-0 d-flex gap-2">
                                        <i className="bi bi-info-circle text-success fs-5"></i>
                                        <div className="small">Wallet balance can be used for <b>promotions</b> and <b>listing upgrades</b>.</div>
                                    </div>
                                </div>
                                <div className="modal-footer px-4 py-3">
                                    <button className="btn btn-outline-secondary px-4" onClick={() => setShowAddFund(false)}>Cancel</button>
                                    <button className="btn btn-success px-4" disabled={!addAmount || Number(addAmount) < 10 || payLoading} onClick={handleAddFund}>
                                        {payLoading ? <><span className="spinner-border spinner-border-sm me-2"></span>Redirecting...</> : <><i className="bi bi-arrow-right-circle me-1"></i>Proceed to Pay</>}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="modal-backdrop show"></div>
                </>
            )}

            <style>{`
                .wl-wrap { padding: 4px; max-width: 1100px; margin: 0 auto; }
                .wl-hero { display:flex; justify-content:space-between; align-items:center; gap:16px; flex-wrap:wrap; background:linear-gradient(135deg,#e8f0ff,#fefefe); border-left:6px solid #4a90e2; color:#0b1220; border-radius:14px; padding:22px 24px; margin-bottom:18px; box-shadow:0 4px 14px rgba(0,0,0,0.05); }
                .wl-hero-left { display:flex; align-items:center; gap:16px; }
                .wl-hero-icon { width:58px; height:58px; border-radius:14px; background:#eef3ff; color:#2563eb; display:flex; align-items:center; justify-content:center; font-size:1.7rem; }
                .wl-hero-label { font-size:.78rem; text-transform:uppercase; letter-spacing:.05em; color:#6b7280; }
                .wl-hero-amount { font-size:2.2rem; font-weight:800; line-height:1.1; color:#16a34a; }
                .wl-hero-sub { font-size:.8rem; color:#6b7280; }
                .wl-hero-actions { display:flex; gap:10px; }
                .wl-btn { border:0; border-radius:24px; padding:9px 18px; font-weight:600; font-size:.92rem; cursor:pointer; transition:.2s; display:inline-flex; align-items:center; }
                .wl-btn-solid { background:#16a34a; color:#fff; } .wl-btn-solid:hover { background:#138a3e; }
                .wl-btn-ghost { background:#fff; color:#2563eb; border:1px solid #cdd9ff; } .wl-btn-ghost:hover { background:#eef3ff; }

                .wl-card { background:#fff; border-radius:16px; box-shadow:0 6px 20px rgba(0,0,0,0.06); overflow:hidden; }
                .wl-card-head { display:flex; justify-content:space-between; align-items:center; padding:16px 18px; border-bottom:1px solid #f0f2f5; font-weight:700; color:#1f2937; }
                .wl-count { background:#eef3ff; color:#2563eb; border-radius:20px; padding:2px 12px; font-size:.8rem; }
                .wl-table { width:100%; border-collapse:separate; border-spacing:0; }
                .wl-table thead th { background:#f7f9fc; color:#6b7280; font-size:12px; text-transform:uppercase; letter-spacing:.04em; font-weight:700; padding:12px 16px; border-bottom:1px solid #eef0f3; }
                .wl-table tbody td { padding:14px 16px; border-bottom:1px solid #f3f5f8; vertical-align:middle; font-size:14px; }
                .wl-table tbody tr:hover { background:#f7faff; }
                .wl-txn-icon { width:38px; height:38px; flex:0 0 38px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:1.05rem; }
                .wl-txn-icon.credit { background:#e7f8ef; color:#15803d; } .wl-txn-icon.debit { background:#fdecec; color:#dc2626; }
                .wl-pill { display:inline-flex; font-size:12px; font-weight:600; padding:4px 10px; border-radius:20px; }
                .wl-pill.ok { background:#e7f8ef; color:#15803d; } .wl-pill.pend { background:#fff4e0; color:#b45309; } .wl-pill.fail { background:#fdecec; color:#dc2626; }
                .wl-spinner { width:48px; height:48px; border-radius:50%; border:5px solid #e3eaff; border-top-color:#2563eb; animation:wlspin .9s linear infinite; margin:0 auto; }
                @keyframes wlspin { to { transform:rotate(360deg); } }
                .custom-toast { position:fixed; top:20px; right:20px; min-width:320px; max-width:420px; background:#fff; border-radius:10px; padding:14px 16px; box-shadow:0 12px 30px rgba(0,0,0,0.12); display:flex; align-items:center; gap:12px; z-index:9999; animation:slideIn .35s ease-out; }
                .custom-toast.success { border-left:5px solid #22c55e; } .custom-toast.danger { border-left:5px solid #ef4444; } .custom-toast.info { border-left:5px solid #3b82f6; }
                .toast-icon { width:34px; height:34px; border-radius:50%; display:flex; align-items:center; justify-content:center; font-weight:bold; font-size:16px; color:#fff; }
                .custom-toast.success .toast-icon { background:#22c55e; } .custom-toast.danger .toast-icon { background:#ef4444; } .custom-toast.info .toast-icon { background:#3b82f6; }
                .toast-content { flex:1; } .toast-title { font-weight:600; font-size:14px; margin-bottom:2px; } .toast-message { font-size:13px; color:#555; }
                .toast-close { border:none; background:transparent; font-size:18px; cursor:pointer; color:#999; } .toast-close:hover { color:#000; }
                .toast-progress { position:absolute; bottom:0; left:0; height:3px; background:linear-gradient(90deg,#60a5fa,#22c55e); animation:progress linear forwards; width:100%; }
                @keyframes slideIn { from { opacity:0; transform:translateX(30px); } to { opacity:1; transform:translateX(0); } }
                @keyframes progress { from { width:100%; } to { width:0%; } }
                @media (max-width:640px){ .wl-hero{flex-direction:column; align-items:flex-start;} .wl-hero-actions{width:100%;} }
            `}</style>
        </div>
    );
}
